import RouteTitleHandler from '../components/common/RouteTitleHandler';
import Layout from '../components/Layout';
import ACGLogoSignature from '../assets/acg-logo-with-signature.png';
import Focus360Component from '../components/Focus360Component';

import { Download } from 'lucide-react';
import { useParams } from 'react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useModuleContext } from '../context/ModuleContext';
import { useSidebarContext } from '../context/SidebasContext';
import { useChatHeadersContext } from '../context/ChatHeadersContext';
import { toast } from 'react-hot-toast';
import { fetchCategories } from '../services/categoryService';
import { fetchChatMessagesWithFeedback } from '../services/chatService';
import CommonTextBoxArea from '../components/CommonTextArea';
import UserMessage from '../components/UserMessage';
import JsonTableModal from '../components/modals/JsonTableModal';
import BotMessage from '../components/BotMessage';
import TypingLoader from '../components/common/TypingLoader';
import AdditionalTagsForSmartAssistant from '../components/AdditionalTagsForSmartAssistant';
import { generateFocus360PDF } from '../utils/pdfGenerator';
import MarkdownModal from '../components/modals/MarkdownModal';


export default function Chat() {
    const DEFAULT_CATEGORY = 'Calendar';
    const DEFAULT_SUBCATEGORY = 'Troubleshooting';
    const { id } = useParams();
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);
    const [isStreaming, setIsStreaming] = useState(false);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatTitle, setChatTitle] = useState(null);
    const [isUserScrolling, setIsUserScrolling] = useState(false);
    const [currentJsonData, setCurrentJsonData] = useState(null);
    const [isModuleUpdateInProgress, setIsModuleUpdateInProgress] = useState(false);
    const [hasInitialDataLoaded, setHasInitialDataLoaded] = useState(false);

    const { selectedModule, setSelectedModule, getModuleConfig, getAllModuleConfigs, getAvailableModules } = useModuleContext();
    const chatScrollRef = useRef(null);
    const { triggerRefresh } = useSidebarContext();
    const scrollContainerRef = useRef(null);
    const { getChatHeader } = useChatHeadersContext();
    const currentChatIdRef = useRef(id);

    // Memoize the setCurrentJsonData function to prevent unnecessary re-renders
    const handleJsonData = useCallback((data) => {
        setCurrentJsonData(data);
    }, []);

    /** Insights live under `data` on the API envelope; flat legacy payloads use the root. */
    const focusPayload = useMemo(
        () => (currentJsonData ? currentJsonData.data ?? currentJsonData : null),
        [currentJsonData]
    );

    const updateLastAssistantMessage = (content) => {
        setChatMessages((prev) => {
            const updated = [...prev];
            const lastIdx = updated.length - 1;
            if (updated[lastIdx]?.role === 'assistant') {
                updated[lastIdx] = { ...updated[lastIdx], content, isLoading: false };
            }
            return updated;
        });
        setIsStreaming(false);
        setTimeout(() => autoScrollToBottom(), 50);
    };

    const autoScrollToBottom = useCallback(() => {
        if (chatScrollRef.current) {
            chatScrollRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, []);

    // Load initial data (languages and categories) only once per module
    useEffect(() => {
        const availableModules = getAvailableModules();
        const modulesSupportingCategories = ['smart-assistant', 'oee', 'energy-optimization']; // modules that use categories
        
        // Only load categories for active modules that support categories (exclude focus360)
        const shouldLoadCategories = availableModules.includes(selectedModule) && 
                                   modulesSupportingCategories.includes(selectedModule);
        
        if (!shouldLoadCategories) return;
        if (isModuleUpdateInProgress) return; // Skip if module update is in progress
        if (hasInitialDataLoaded) return; // Skip if we've already loaded data for this module

        const loadInitialData = async () => {
            try {
                const [categories] = await Promise.all([
                    fetchCategories(selectedModule)
                ]);
                setCategories(categories);
                setHasInitialDataLoaded(true);
            } catch (error) {
                console.error('Initialization failed:', error);
                toast.error('Failed to load categories. Please refresh the page.', {
                    duration: 4000,
                });
            }
        };
        loadInitialData();
    }, [selectedModule, isModuleUpdateInProgress, hasInitialDataLoaded, getAvailableModules]);

    // Reset hasInitialDataLoaded when chat ID changes
    useEffect(() => {
        setHasInitialDataLoaded(false);
    }, [id]);

    useEffect(() => {
        const loadChatHistory = async () => {
            // Skip fetching if we're in the middle of a module update
            if (isModuleUpdateInProgress) {
                return;
            }

            if (id) {
                // Check if this is a brand new chat (we have messages but no previous chat ID)
                const isBrandNewChat = chatMessages.length > 0 && !currentChatIdRef.current;

                // Clear messages unless it's a brand new chat we just created
                if (!isBrandNewChat) {
                    setChatMessages([]);
                    setChatTitle(null);
                }

                // Update the current chat ID ref
                currentChatIdRef.current = id;

                // Set chat title from headers if available
                const headerTitle = getChatHeader(id);
                if (headerTitle) {
                    setChatTitle(headerTitle);
                }

                // Always try to fetch messages for existing chats (unless it's a brand new chat)
                if (!isBrandNewChat) {
                    try {
                        const { messages, messageApplicationId, messageCategory, messageSubcategory } = await fetchChatMessagesWithFeedback(id, selectedModule);

                        // If we got a messageApplicationId and it's different from current module's application_id
                        if (messageApplicationId) {
                            const currentApplicationId = getModuleConfig(selectedModule).applicationId;
                            if (messageApplicationId !== currentApplicationId) {
                                // Find the module that matches this application_id
                                const matchingModule = Object.entries(getAllModuleConfigs()).find(
                                    ([_, config]) => config.applicationId === messageApplicationId
                                );

                                // If we found a matching module, update the context
                                if (matchingModule) {
                                    setIsModuleUpdateInProgress(true);
                                    setSelectedModule(matchingModule[0]);
                                    // console.log(`Updated module context to ${matchingModule[0]} based on message application_id`);
                                    // Reset the flag after a short delay to ensure state updates are complete
                                    setTimeout(() => setIsModuleUpdateInProgress(false), 0);
                                }
                            }
                        }

                        // Update category and subcategory from the API response if available
                        if (messageCategory) {
                            setSelectedCategory(messageCategory);
                        }
                        if (messageSubcategory) {
                            setSelectedSubcategory(messageSubcategory);
                        }

                        if (Array.isArray(messages) && messages.length > 0) {
                            setChatMessages(messages);
                        }
                    } catch (error) {
                        console.error('Failed to fetch chat messages with feedback:', error);
                    }
                }
            } else {
                // Clear messages when not on a specific chat page
                setChatMessages([]);
                setChatTitle("New chat");
                currentChatIdRef.current = undefined;
                // Clear JSON data when going to home page
                setCurrentJsonData(null);
            }
        };
        loadChatHistory();
    }, [id]);

    const handleEnergyOptimizationStream = (chunk) => {
        setChatMessages((prev) => {
            const updated = [...prev];
            const lastIdx = updated.length - 1;

            if (updated[lastIdx]?.role === 'assistant') {
                // If this is the first chunk and we're still loading, replace it
                if (updated[lastIdx].isLoading) {
                    updated[lastIdx] = {
                        ...updated[lastIdx],
                        content: chunk,
                        isLoading: false,
                    };
                } else {
                    // Otherwise append like normal
                    updated[lastIdx] = {
                        ...updated[lastIdx],
                        content: (updated[lastIdx].content || '') + chunk,
                    };
                }
            }
            return updated;
        });

        // Only auto-scroll if user hasn't manually scrolled or is already at bottom
        if (!isUserScrolling || isAtBottom()) {
            setTimeout(() => autoScrollToBottom(), 50);
        }
    };

    const handleHeaderCreated = (header, conversationId) => {
        setChatTitle(header);
    };

    const handleStreamComplete = (conversationId, pairId, images) => {
        // Reset loading state when stream actually completes
        setIsStreaming(false);

        // Update the last assistant message with pair_id and images if provided
        setChatMessages((prev) => {
            const updated = [...prev];
            const lastIdx = updated.length - 1;

            if (updated[lastIdx]?.role === 'assistant') {
                updated[lastIdx] = {
                    ...updated[lastIdx],
                    pairId: pairId || updated[lastIdx].pairId,
                    images: images || updated[lastIdx].images || [],
                    feedback: updated[lastIdx].feedback || null, // Ensure feedback is explicitly null for new messages
                };
            }
            return updated;
        });

        // For new chats, trigger sidebar refresh to show the new chat in the list
        // We can determine if it's a new chat by checking if we don't have an ID in the URL params
        const isNewChat = !id;
        if (isNewChat) {
            triggerRefresh();
            // console.log('Stream completed for new conversation:', conversationId, pairId ? `with pairId: ${pairId}` : '- Sidebar refreshed');
        } else {
            console.log('Stream completed for existing conversation:', conversationId, pairId ? `with pairId: ${pairId}` : '');
        }
    };

    useEffect(() => {
        chatScrollRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatMessages]);

    const isAtBottom = () => {
        if (!chatScrollRef.current) return true;
        return chatScrollRef.current.scrollTop + chatScrollRef.current.clientHeight >= chatScrollRef.current.scrollHeight;
    };

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) return;

        let scrollTimeout;
        let lastScrollTop = scrollContainer.scrollTop;

        const handleScroll = () => {
            const currentScrollTop = scrollContainer.scrollTop;

            // Only set user scrolling if there was actual manual scroll movement
            if (Math.abs(currentScrollTop - lastScrollTop) > 5) {
                setIsUserScrolling(true);
                lastScrollTop = currentScrollTop;
            }

            // Clear previous timeout
            clearTimeout(scrollTimeout);

            // Reset user scrolling flag after they stop scrolling
            scrollTimeout = setTimeout(() => {
                setIsUserScrolling(false);
            }, 300); // Reduced timeout for better responsiveness
        };

        scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            scrollContainer.removeEventListener('scroll', handleScroll);
            clearTimeout(scrollTimeout);
        };
    }, []);

    return (
        <Layout>
            <RouteTitleHandler title="Chat" />
            <div className="w-full h-full flex flex-col items-center justify-center transition-all duration-300">
                <section className={`${id || chatMessages.length > 0 ? 'flex grow' : 'hidden'} w-full pt-2`}>
                    <div
                        ref={scrollContainerRef}
                        className={`flex flex-col grow overflow-auto w-full px-3 sm:px-6 space-y-4 scroll-smooth ${selectedModule === 'focus360' ? 'max-h-[60vh]' : 'max-h-[76vh]'
                            }`}
                    >
                        <div className="mx-auto w-full max-w-4xl min-w-0 h-fit p-1 px-3 space-y-4">
                            {chatTitle && (
                                <div className="text-center mb-6">
                                    <div className="bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-blue-600/10 backdrop-blur-md border border-white/20 rounded-2xl mx-auto w-fit px-6 py-3 shadow-lg">
                                        <h2 className="text-lg font-medium text-gray-100 tracking-wide">{chatTitle}</h2>
                                    </div>
                                </div>
                            )}
                            {chatMessages.map((msg, idx) =>
                                msg.role === 'user' ? (
                                    <UserMessage key={idx} content={msg.content} pairId={msg.pairId} />
                                ) : msg.isLoading ? (
                                    <TypingLoader key={idx} />
                                ) : (
                                    <BotMessage
                                        key={idx}
                                        content={msg.content}
                                        citations={msg.citations}
                                        images={msg.images}
                                        pairId={msg.pairId}
                                        feedback={msg.feedback}
                                        reportCustomerName={msg.reportCustomerName}
                                        reportLocation={msg.reportLocation}
                                        reportSiteInfo={msg.reportSiteInfo}
                                        onJsonData={handleJsonData}
                                    />
                                )
                            )}
                        </div>
                        {/* {isStreaming && <div className="bg-gray-200 size-4 animate-pulse"></div>} */}
                        <div ref={chatScrollRef} />
                    </div>
                </section>

                {/* UI Table Triggers - shown below chat messages only when not on a specific chat page */}
                {currentJsonData && (
                    <section className="w-full px-3 sm:px-6 mt-4">
                        <div className="mx-auto w-full max-w-4xl min-w-0 p-1 px-3">
                            <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg p-4 w-full border border-gray-700/50">
                                <h3 className="text-sm font-medium text-gray-300 mb-3">Quick Actions & Data Views</h3>
                                <div className="flex flex-wrap gap-3">
                                    <JsonTableModal
                                        data={focusPayload?.pending_payments?.pending_payments_detailed_table_due}
                                        title="Pending Payments"
                                        triggerLabel="Pending Payments Due"
                                    />
                                    <JsonTableModal
                                        data={focusPayload?.pending_payments?.pending_payments_detailed_table_credit}
                                        title="Pending Payments Credit"
                                        triggerLabel="Pending Payments Credit"
                                    />
                                    <JsonTableModal
                                        data={focusPayload?.dispatch_status?.dispatch_status_table}
                                        title="Dispatch Status"
                                        triggerLabel="Dispatch Data"
                                    />
                                    <MarkdownModal
                                        content={focusPayload?.opportunity_results?.opportunity_detailed_table}
                                        title="Opportunity Detail"
                                        triggerLabel="Opportunity Data"
                                    />
                                    <button
                                        onClick={async () => {
                                            try {
                                                toast.loading('Generating PDF report...', { id: 'pdf-generation' });

                                                const customerName =
                                                    (typeof currentJsonData.customer_name === 'string' &&
                                                        currentJsonData.customer_name.trim()) ||
                                                    'Focus360 Customer';
                                                const location =
                                                    typeof currentJsonData.location === 'string'
                                                        ? currentJsonData.location
                                                        : '';

                                                await generateFocus360PDF(currentJsonData, customerName, location);

                                                toast.success('PDF report generated successfully!', { id: 'pdf-generation' });
                                            } catch (error) {
                                                console.error('PDF generation failed:', error);
                                                toast.error('Failed to generate PDF report. Please try again.', { id: 'pdf-generation' });
                                            }
                                        }}
                                        className="inline-flex text-sm items-center border border-blue-400/50 gap-2 text-white bg-blue-900 hover:bg-blue-900/50 px-3 py-1 rounded-full transition duration-300 cursor-pointer"
                                    >
                                        <Download size={16} />
                                        Download report
                                    </button>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                <div className={`w-full flex flex-col items-center px-3 md:px-0 ${id ? 'mb-4' : ''}`}>
                    {!id && (
                        <div className="flex flex-col items-center mb-3">
                            <img src={ACGLogoSignature} alt="ACG logo" className="w-fit h-[74px] mb-3" />
                            <p className="text-xl font-medium text-slate-500">What can I help you with?</p>
                        </div>
                    )}

                    {/* Show CommonTextBoxArea when there's a chat ID (regardless of module) */}
                    {id && (
                        <CommonTextBoxArea
                            id={id}
                            category={selectedCategory || DEFAULT_CATEGORY}
                            subcategory={selectedSubcategory || DEFAULT_SUBCATEGORY}
                            showCategoryAndSubcategory={selectedModule === 'smart-assistant'}
                            onMessageAdd={(message) => {
                                setChatMessages((prev) => [...prev, message]);
                                if (message.role === 'user') setIsStreaming(true);
                            }}
                            onStreamUpdate={updateLastAssistantMessage}
                            onStreamUpdateEnergyOptimization={handleEnergyOptimizationStream}
                            onHeaderCreated={handleHeaderCreated}
                            onStreamComplete={handleStreamComplete}
                        />
                    )}

                    {/* Module-specific components only show when there's no ID */}
                    {!id && selectedModule === 'smart-assistant' && (
                        <>
                            <CommonTextBoxArea
                                id={id}
                                category={selectedCategory || DEFAULT_CATEGORY}
                                subcategory={selectedSubcategory || DEFAULT_SUBCATEGORY}
                                onMessageAdd={(message) => {
                                    setChatMessages((prev) => [...prev, message]);
                                    if (message.role === 'user') setIsStreaming(true);
                                }}
                                onStreamUpdate={updateLastAssistantMessage}
                                onStreamUpdateEnergyOptimization={handleEnergyOptimizationStream}
                                onHeaderCreated={handleHeaderCreated}
                                onStreamComplete={handleStreamComplete}
                            />
                            <AdditionalTagsForSmartAssistant
                                categories={categories}
                                onSelectionChange={(category, subcategory) => {
                                    setSelectedCategory(category);
                                    setSelectedSubcategory(subcategory);
                                }}
                            />
                        </>
                    )}

                    {!id && selectedModule === 'focus360' && (
                        <Focus360Component onMessageAdd={(message) => setChatMessages((prev) => [...prev, message])} />
                    )}

                    {!id && selectedModule === 'energy-optimization' && (
                        <CommonTextBoxArea
                            onMessageAdd={(message) => {
                                setChatMessages((prev) => [...prev, message]);
                                if (message.role === 'user') {
                                    setIsStreaming(true);
                                }
                            }}
                            onStreamUpdateEnergyOptimization={handleEnergyOptimizationStream}
                            onHeaderCreated={handleHeaderCreated}
                            onStreamComplete={handleStreamComplete}
                        />
                    )}

                    {!id && selectedModule === 'oee' && (
                        <CommonTextBoxArea
                            category={selectedCategory || DEFAULT_CATEGORY}
                            subcategory={selectedSubcategory || DEFAULT_SUBCATEGORY}
                        />
                    )}
                </div>
            </div>
        </Layout>
    );
}